import type { ThemeConfig } from 'antd';
import { color, font } from './tokens';

/**
 * antd theme for Tempo (Ink & Coral, coral-led). Values come from tokens.ts.
 *
 * Global colorPrimary is ink, so selection controls (checkboxes, radios, switches, date ranges,
 * pagination, selected toggles) are ink. Links and tabs carry the brand coral, and primary *buttons*
 * are overridden to the logo coral with ink text. Iris is reserved for the "Submitted" status.
 */
export const tempoTheme: ThemeConfig = {
  token: {
    colorPrimary: color.ink,
    colorInfo: color.ink,
    // antd derives light shades from the seed colors; from a near-black seed those come out dark grey
    // (info notes, date ranges, selected menu items), so the light shades are set explicitly.
    colorPrimaryBg: color.coralTint,
    colorPrimaryBgHover: color.coralSoft,
    colorPrimaryBorder: color.borderStrong,
    colorPrimaryBorderHover: color.textMuted,
    colorPrimaryHover: color.textSecondary,
    colorPrimaryActive: color.ink,
    colorPrimaryText: color.ink,
    colorPrimaryTextHover: color.textSecondary,
    colorPrimaryTextActive: color.ink,
    colorInfoBg: color.surfaceAlt,
    colorInfoBgHover: color.fill,
    colorInfoBorder: color.border,
    colorInfoBorderHover: color.borderStrong,
    colorInfoHover: color.textSecondary,
    colorInfoActive: color.ink,
    colorInfoText: color.text,
    colorInfoTextHover: color.text,
    colorInfoTextActive: color.text,
    colorSuccess: color.success,
    colorWarning: color.warning,
    colorError: color.danger,
    colorLink: color.coralText,
    colorLinkHover: color.coralDeep,
    colorLinkActive: color.coralDeep,

    colorText: color.text,
    colorTextHeading: color.text,
    colorTextSecondary: color.textSecondary,
    colorTextTertiary: color.textMuted,
    colorTextQuaternary: color.textDisabled,
    colorTextDescription: color.textMuted,

    colorBgLayout: color.canvas,
    colorBgContainer: color.surface,
    colorBgElevated: color.surface,
    colorBgSpotlight: color.ink,
    colorFillAlter: color.surfaceAlt,
    colorBorder: color.borderStrong,
    colorBorderSecondary: color.border,
    colorSplit: color.divider,

    fontFamily: font.ui,
    fontSize: 14,
    fontSizeHeading1: 32,
    fontSizeHeading2: 26,
    fontSizeHeading3: 21,
    fontSizeHeading4: 18,
    fontSizeHeading5: 16,
    fontWeightStrong: 600,

    borderRadius: 6,
    borderRadiusLG: 8,
    borderRadiusSM: 4,
    borderRadiusXS: 2,
    controlHeight: 36,
    controlHeightLG: 44,
    controlHeightSM: 28,

    boxShadow: '0 6px 20px rgba(23, 22, 20, 0.10)',
    boxShadowSecondary: '0 8px 28px rgba(23, 22, 20, 0.12)',
    boxShadowTertiary: 'none',
    wireframe: false,
  },
  components: {
    Layout: {
      siderBg: color.sidebar,
      headerBg: color.surface,
      bodyBg: color.canvas,
      headerHeight: 60,
      headerPadding: '0 24px',
      triggerBg: color.sidebarActive,
      triggerColor: color.white,
    },
    Menu: {
      darkItemBg: color.sidebar,
      darkSubMenuItemBg: color.sidebar,
      darkPopupBg: color.sidebar,
      darkItemColor: color.sidebarText,
      darkItemHoverBg: color.sidebarHover,
      darkItemHoverColor: color.white,
      darkItemSelectedBg: color.sidebarActive,
      darkItemSelectedColor: color.white,
      itemBorderRadius: 6,
      itemMarginInline: 10,
      itemHeight: 40,
      iconSize: 16,
    },
    Button: {
      colorPrimary: color.coral,
      colorPrimaryHover: color.coralHover,
      colorPrimaryActive: color.coralActive,
      primaryColor: color.ink,
      primaryShadow: 'none',
      defaultShadow: 'none',
      dangerShadow: 'none',
      fontWeight: 600,
      defaultColor: color.text,
      defaultBorderColor: color.borderStrong,
      defaultHoverColor: color.ink,
      defaultHoverBorderColor: color.ink,
      defaultActiveColor: color.ink,
      defaultActiveBorderColor: color.ink,
    },
    Badge: {
      colorError: color.coral,
      colorTextLightSolid: color.ink,
    },
    Card: {
      headerFontSize: 15,
      headerHeight: 52,
      boxShadowTertiary: 'none',
    },
    Table: {
      headerBg: color.surfaceAlt,
      headerColor: color.textSecondary,
      headerSplitColor: 'transparent',
      headerSortActiveBg: color.fill,
      headerSortHoverBg: color.fill,
      borderColor: color.divider,
      rowHoverBg: '#FAFAF8',
      rowSelectedBg: color.coralTint,
      rowSelectedHoverBg: color.coralSoft,
      footerBg: color.surfaceAlt,
      bodySortBg: '#FAFAF8',
      cellPaddingBlock: 12,
    },
    Tabs: {
      inkBarColor: color.coral,
      itemColor: color.textSecondary,
      itemSelectedColor: color.text,
      itemHoverColor: color.text,
      itemActiveColor: color.text,
    },
    Segmented: {
      trackBg: color.fill,
      itemColor: color.textSecondary,
      itemHoverColor: color.text,
      itemSelectedBg: color.ink,
      itemSelectedColor: color.white,
    },
    Tag: {
      defaultBg: color.surface,
      defaultColor: color.textSecondary,
    },
    Input: {
      hoverBorderColor: color.textMuted,
      activeBorderColor: color.ink,
      activeShadow: '0 0 0 3px rgba(247, 125, 105, 0.18)',
    },
    InputNumber: {
      hoverBorderColor: color.textMuted,
      activeBorderColor: color.ink,
      activeShadow: '0 0 0 3px rgba(247, 125, 105, 0.18)',
    },
    Select: {
      hoverBorderColor: color.textMuted,
      activeBorderColor: color.ink,
      activeOutlineColor: 'rgba(247, 125, 105, 0.18)',
      optionSelectedBg: color.coralTint,
    },
    DatePicker: {
      cellActiveWithRangeBg: color.coralTint,
      cellHoverWithRangeBg: color.coralSoft,
      cellRangeBorderColor: color.coral,
      hoverBorderColor: color.textMuted,
      activeBorderColor: color.ink,
      activeShadow: '0 0 0 3px rgba(247, 125, 105, 0.18)',
    },
    Progress: {
      defaultColor: color.ink,
      remainingColor: color.fill,
    },
    Modal: {
      titleFontSize: 17,
    },
    Statistic: {
      contentFontSize: 28,
    },
  },
};

export default tempoTheme;
